import { Component } from "@angular/core";
import { environment } from "environment";
import { IdleTimeoutEvent, IdleTimerService } from "./common/services/idle-timer.service";
import { RouteTrackingService } from "./common/services/route-tracking.service";
import { StorageService } from "./common/services/storage.service";
import { AuthService } from "./services/auth.service";
import { IdleTimerConstants } from "./common/constants/idle-timer.constants";
import { filter, Subject, takeUntil } from "rxjs";
import { LayoutService } from "./services/layout.service";
import { NavigationEnd, Router } from "@angular/router";
import { MyZpaxService } from "./services/myzpax.service";
import { isFallbackRoute, isNoLayoutRoute } from "./common/utils/myzpax-routes.util";
import { navigateHome } from "./common/utils/home-route.util";
import { appRoute } from "./app-route.constants";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent {
  private destroy$ = new Subject<void>();
  private unsubscribeManualLock?: () => void;
  private unsubscribeLastInteraction?: () => void;
  isNoLayoutRoute = this.myzpaxService.isInMyzpax ? isNoLayoutRoute(window.location.pathname) : false;
  isPhysican: boolean;
  constructor(
    public storageService: StorageService,
    private routeTrackingService: RouteTrackingService,
    private idleTimerService: IdleTimerService,
    private authService: AuthService,
    public layoutService : LayoutService,
    private router: Router,
    private myzpaxService: MyZpaxService
  ) {
    this.isPhysican = false;
    window.onpageshow = function (event) {
      if (event.persisted) {
        window.location.reload();
      }
    };
    
    this.router.events.pipe(
      takeUntil(this.destroy$),
      filter((event) => event instanceof NavigationEnd)
    ).subscribe(() => {
      if (this.myzpaxService.isInMyzpax) {
        this.isNoLayoutRoute = isNoLayoutRoute(window.location.pathname);
      }
    });
  }

  ngOnInit(): void {
    this.routeTrackingService.init();

    this.layoutService.mobileToDesktop$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const hasSession = this.storageService.getUser() !== null;
        const wasLockedDueToIdle = this.storageService.getLastActiveUserId() !== null;

        // Without a session there is nothing to hand to React — it would bounce
        // straight back to Angular — so keep unauthenticated users on the
        // Angular route whose guard drives the SSO login.
        if (!hasSession) {
          this.router.navigate([appRoute.home], { replaceUrl: true });
          return;
        }

        if (!wasLockedDueToIdle) {
          navigateHome(this.router, true);
        }
      });

    this.storageService.isUserPhysician.subscribe((val) => {
      this.isPhysican = val;
    });

    // Fallback routes (e.g. /marketing) have no session to time out, so idle
    // tracking is skipped there — but the manual lock button is still shown
    // and must keep working, so its listener is wired unconditionally.
    if (this.myzpaxService.isInMyzpax) {
      this.unsubscribeManualLock = this.myzpaxService.onManualLock(() =>
        this.authService.lockUserSession('manual'));
    }

    if (!isFallbackRoute(window.location.pathname)) {
      this.setIdleTimer();

      if (this.myzpaxService.isInMyzpax) {
        // myzPAX also pushes last_interaction unsolicited (e.g. on tab focus /
        // after reauth) — re-evaluate idle state on every such update.
        this.unsubscribeLastInteraction = this.myzpaxService.onLastInteraction((timestamp) =>
          this.evaluateIdleFromLastInteraction(timestamp));

        this.checkLastInteraction();
      }
    }
  }

  setIdleTimer() {
      if (!!environment.idleTimeBeforeLocking) {
        const timers: IdleTimeoutEvent[] = [
          { time: environment.idleTimeBeforeLocking, message: IdleTimerConstants.lockUser },
        ];
        if (!this.myzpaxService.isInMyzpax && !!environment.idleTimeBeforeTerminating) {
          timers.push({ time: environment.idleTimeBeforeTerminating, message: IdleTimerConstants.terminateUser });
        }
        this.idleTimerService.setTimers(timers).subscribe((timerData) => {
            this.idleTimerService.stop();
            if (timerData.message === IdleTimerConstants.lockUser) {
              this.timeoutMotomoLog(timerData);
              this.authService.lockUserSession('timeout');
            } else if (timerData.message === IdleTimerConstants.terminateUser) {
              this.timeoutMotomoLog(timerData);
              this.authService.logout();
            }
          });
      }
    }

    async checkLastInteraction() {
      const lastInteraction = await this.myzpaxService.getLastInteraction();
      this.evaluateIdleFromLastInteraction(lastInteraction);
    }

    private evaluateIdleFromLastInteraction(lastInteraction: number) {
      if (this.storageService.getUser() === null) {
        return;
      }
      if ((Date.now() - lastInteraction) > (environment.idleTimeBeforeLocking * 1000)) {
        this.authService.lockUserSession('timeout');
      }
    }

    ngOnDestroy() {
      this.idleTimerService.stop();
      this.unsubscribeManualLock?.();
      this.unsubscribeLastInteraction?.();
      this.destroy$.next();
      this.destroy$.complete();
    }

    timeoutMotomoLog(timerData: any){
      const userJson = sessionStorage.getItem('user');
      let userId = 'anonymous';
      let username = 'anonymous';
      if (userJson) {
        try {
          const user = JSON.parse(userJson);
          userId = user.id || 'anonymous';
          username = user.fullname || 'anonymous';
        } catch (err) {
          console.warn('Failed to parse user from sessionStorage', err);
        }
      }

      if (window._paq) {
        window._paq.push([
          'trackEvent',
          'idle Timeout',
          'Logout',
          `${timerData.message} | ${userId}/${username}`
        ]);
      }
    }
}
